import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  RotateCcw, ArrowLeft, ArrowRight, Loader2, CheckCircle2, X,
  Trophy, Volume2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import {
  fetchAttemptedVocab, saveProgress, submitQuizSession,
  type VocabWord
} from "@/lib/queries";
import { playWordClick, speakWord } from "@/lib/audio";
import { cn } from "@/lib/utils";

const SESSION_SIZE = 5;
const POINTS_PER_GOT_IT = 5;

type Phase = "loading" | "no-vocab" | "playing" | "results";
type CardDir = "ar-en" | "en-ar";

export default function Revise() {
  const nav = useNavigate();
  const [phase, setPhase] = React.useState<Phase>("loading");
  const [cards, setCards] = React.useState<VocabWord[]>([]);
  const [cardDirs, setCardDirs] = React.useState<CardDir[]>([]);
  const [idx, setIdx]     = React.useState(0);
  const [flipped, setFlipped] = React.useState(false);

  const [gotIt, setGotIt] = React.useState(0);
  const [marked, setMarked] = React.useState<Set<number>>(new Set());

  const startRef = React.useRef<Date>(new Date());
  const [submitting, setSubmitting] = React.useState(false);
  const [awarded, setAwarded] = React.useState<{ new_total: number; points_awarded: number } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      const attempted = await fetchAttemptedVocab();
      if (cancelled) return;
      if (attempted.length === 0) { setPhase("no-vocab"); return; }

      const picks = shuffle([...attempted]).slice(0, SESSION_SIZE);
      const dirs  = picks.map<CardDir>(() => (Math.random() < 0.5 ? "ar-en" : "en-ar"));
      setCards(picks);
      setCardDirs(dirs);
      setIdx(0);
      setFlipped(false);
      setGotIt(0);
      setMarked(new Set());
      startRef.current = new Date();
      setPhase("playing");
      if (dirs[0] === "ar-en" && picks[0]) void speakWord(picks[0]);
    })();
    return () => { cancelled = true; };
  }, []);

  // Keyboard nav.
  React.useEffect(() => {
    if (phase !== "playing") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") move(1);
      else if (e.key === "ArrowLeft") move(-1);
      else if (e.key === " ") { e.preventDefault(); setFlipped(f => !f); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx, flipped]);

  function move(delta: number) {
    const ni = idx + delta;
    if (ni < 0 || ni >= cards.length) return;
    setIdx(ni);
    setFlipped(false);
    if (cardDirs[ni] === "ar-en" && cards[ni]) void speakWord(cards[ni]);
  }

  async function endRound() {
    setPhase("results");
    setSubmitting(true);
    try {
      const r = await submitQuizSession({
        claimed_correct: gotIt,
        session_started: startRef.current,
        mode:            "revise"
      });
      if (r) setAwarded(r);
    } catch (e) { console.warn("[revise] submit", e); }
    setSubmitting(false);
  }

  function mark(right: boolean) {
    if (marked.has(idx)) return;
    setMarked(prev => new Set(prev).add(idx));
    const c = cards[idx];
    if (c) {
      void saveProgress({
        word_id: c.id, correct: right, mode: "revise",
        points:  right ? POINTS_PER_GOT_IT : 0
      });
    }
    if (right) setGotIt(g => g + 1);
    // Auto-advance after 350ms; if last card, go to results.
    window.setTimeout(() => {
      if (idx + 1 >= cards.length) void endRound();
      else move(1);
    }, 350);
  }

  const card = cards[idx];
  const dir = cardDirs[idx];
  const showArOnFront = dir === "ar-en";

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[900px] pointer-events-none" density={40} />

        <div className="relative mx-auto max-w-3xl px-4 md:px-6 pt-10 pb-32">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </Button>
            {phase === "playing" && <Badge variant="accent">Card {idx + 1} / {cards.length}</Badge>}
          </div>

          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <RotateCcw className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Revise</h1>
            </div>
            <p className="text-fg-2"><span className="text-accent">Mixed topics</span> · {POINTS_PER_GOT_IT} pts per "Got it"</p>
          </BlurReveal>

          {phase === "playing" && (
            <div className="mt-4 h-1.5 rounded-full bg-bg-2 overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${(idx / cards.length) * 100}%` }} />
            </div>
          )}

          <div className="mt-8">
            {phase === "loading" && <Spinner />}

            {phase === "no-vocab" && (
              <Card className="text-center"><CardContent className="py-12">
                <p className="text-fg-2">Nothing to revise yet — practise a topic first.</p>
                <Button className="mt-4" asChild><Link to="/learn">Pick a topic</Link></Button>
              </CardContent></Card>
            )}

            {phase === "playing" && card && (
              <BlurReveal>
                <Card
                  className={cn(
                    "text-center cursor-pointer min-h-[340px] relative overflow-hidden transition-transform select-none",
                    flipped && "ring-1 ring-accent"
                  )}
                  onClick={() => setFlipped(f => !f)}
                >
                  <div aria-hidden className="absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-accent/15 blur-3xl" />
                  <CardContent className="relative py-12 flex flex-col items-center justify-center min-h-[340px]">
                    {!flipped ? (
                      showArOnFront ? (
                        <>
                          <div className="text-xs uppercase tracking-widest text-fg-3 mb-3">Tap to see English</div>
                          <div className="text-6xl md:text-7xl font-bold text-accent" style={{ fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }}>
                            {card.arabic}
                          </div>
                          <div className="mt-3 text-lg text-fg-2">{card.transliteration}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-xs uppercase tracking-widest text-fg-3 mb-3">Tap to see Arabic</div>
                          <div className="text-4xl md:text-5xl font-bold text-accent">{card.english}</div>
                        </>
                      )
                    ) : showArOnFront ? (
                      <>
                        <div className="text-xs uppercase tracking-widest text-fg-3 mb-3">English</div>
                        <div className="text-4xl md:text-5xl font-bold">{card.english}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs uppercase tracking-widest text-fg-3 mb-3">Arabic</div>
                        <div className="text-6xl md:text-7xl font-bold text-accent" style={{ fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }}>
                          {card.arabic}
                        </div>
                        <div className="mt-3 text-lg text-fg-2">{card.transliteration}</div>
                      </>
                    )}
                    <Button variant="ghost" size="sm" className="mt-5"
                      onClick={e => { e.stopPropagation(); void playWordClick(card); }}>
                      <Volume2 className="w-4 h-4" /> Listen
                    </Button>
                  </CardContent>
                </Card>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <Button variant="outline" size="lg" className="border-bad/40 text-bad hover:bg-bad/10"
                    disabled={marked.has(idx)} onClick={() => mark(false)}>
                    <X className="w-4 h-4" /> Still tricky
                  </Button>
                  <Button size="lg" className="bg-ok text-bg hover:bg-ok/90 hover:text-bg"
                    disabled={marked.has(idx)} onClick={() => mark(true)}>
                    <CheckCircle2 className="w-4 h-4" /> Got it
                  </Button>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <Button variant="ghost" size="sm" disabled={idx === 0} onClick={() => move(-1)}>
                    <ArrowLeft className="w-4 h-4" /> Previous
                  </Button>
                  <div className="text-[11px] text-fg-3">
                    <kbd className="px-1.5 py-0.5 rounded border border-line bg-bg-2">Space</kbd> flip · <kbd className="px-1.5 py-0.5 rounded border border-line bg-bg-2">←</kbd>/<kbd className="px-1.5 py-0.5 rounded border border-line bg-bg-2">→</kbd> nav
                  </div>
                  <Button variant="ghost" size="sm" disabled={idx === cards.length - 1} onClick={() => move(1)}>
                    Next <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </BlurReveal>
            )}

            {phase === "results" && (
              <BlurReveal>
                <Card>
                  <CardContent className="text-center py-10">
                    <div className="text-xs uppercase tracking-widest text-fg-3">Round complete</div>
                    <h2 className="text-4xl font-bold tracking-tight mt-2">
                      {gotIt} / {cards.length} <span className="text-fg-3 font-normal">got it</span>
                    </h2>

                    {submitting ? (
                      <p className="mt-4 text-fg-2 inline-flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-accent" /> Banking points…
                      </p>
                    ) : awarded ? (
                      <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-accent-lo bg-accent/10 px-5 py-3">
                        <Trophy className="w-5 h-5 text-accent" />
                        <span className="text-accent font-bold text-xl">+{awarded.points_awarded.toLocaleString()}</span>
                        <span className="text-xs uppercase tracking-widest text-fg-3">points</span>
                        <span className="text-xs text-fg-2">· new total {awarded.new_total.toLocaleString()}</span>
                      </div>
                    ) : (
                      <p className="mt-4 text-fg-2 text-sm">Points couldn't sync — try again in a moment.</p>
                    )}

                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                      <Button size="lg" onClick={() => nav(0)}><RotateCcw className="w-4 h-4" /> Another round</Button>
                      <Button variant="outline" size="lg" asChild><Link to="/dashboard">Dashboard <ArrowRight className="w-4 h-4" /></Link></Button>
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

function Spinner() {
  return (
    <div className="py-16 flex items-center justify-center gap-2 text-fg-3">
      <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading…
    </div>
  );
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
